import React, { useState } from 'react';
import { Settings, Trash2, AlertTriangle, User as UserIcon, X, Info } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { useAuth } from '@/contexts/AuthContext';
import { clearUserStorageData } from '@/lib/storage';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';

import { SettingsProfile } from './SettingsProfile';
import { SettingsPreferences } from './SettingsPreferences';

export function SettingsModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { user, signOut } = useAuth();
  const [error, setError] = useState('');
  
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const clearHistoryMutation = useMutation({
    mutationFn: async () => {
      await clearUserStorageData(user?.uid);
    },
    onSuccess: () => {
      setShowConfirmClear(false);
      alert('Local history cleared successfully.');
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to clear local history.');
    }
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const idToken = await user?.getIdToken();
      const response = await fetch('/api/account', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }
    },
    onSuccess: async () => {
      await clearUserStorageData(user?.uid);
      await signOut();
      setShowConfirmDelete(false);
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'An error occurred during account deletion.');
    }
  });

  const handleClearHistoryConfirm = () => {
    setError('');
    clearHistoryMutation.mutate();
  };

  const handleDeleteAccountConfirm = () => {
    setError('');
    deleteAccountMutation.mutate();
  };

  return (
    <>
      <ResponsiveOverlay isOpen={isOpen} onClose={onClose} className="p-6 sm:max-w-md w-full max-h-[90vh]">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
              <Settings className="w-5 h-5" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Settings</h2>
          </div>
          <Button 
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close settings"
            className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {error && !showConfirmDelete && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="space-y-8 overflow-y-auto pr-2 pb-6">
          <SettingsProfile user={user} />
          
          <SettingsPreferences 
            user={user}
            setShowConfirmClear={setShowConfirmClear}
            setShowConfirmDelete={setShowConfirmDelete}
          />
        </div>
      </ResponsiveOverlay>

      {/* Confirm Clear History Overlay */}
      <ResponsiveOverlay isOpen={showConfirmClear} onClose={() => setShowConfirmClear(false)} className="p-6 sm:max-w-sm w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Clear History?</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          Are you sure you want to clear your local history? All saved resumes and interactions on this device will be deleted. This action cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => setShowConfirmClear(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleClearHistoryConfirm}
            disabled={clearHistoryMutation.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
          >
            {clearHistoryMutation.isPending ? 'Clearing...' : 'Yes, clear it'}
          </Button>
        </div>
      </ResponsiveOverlay>

      {/* Confirm Delete Account Overlay */}
      <ResponsiveOverlay isOpen={showConfirmDelete} onClose={() => setShowConfirmDelete(false)} className="p-6 sm:max-w-md w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Delete Account</h3>
        </div>
        
        {error && showConfirmDelete && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="space-y-3 mb-6 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            WARNING: Are you sure you want to permanently delete your account?
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            This action <strong className="text-slate-900 dark:text-white">cannot be undone</strong> and you will lose:
          </p>
          <ul className="text-sm text-slate-600 dark:text-slate-400 list-disc pl-5 space-y-1">
            <li>All purchased credits</li>
            <li>Free tier usage tracking</li>
            <li>All saved data and access</li>
          </ul>
        </div>
        
        <div className="flex items-center justify-end gap-3 flex-col sm:flex-row">
          <Button
            variant="ghost"
            onClick={() => setShowConfirmDelete(false)}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAccountConfirm}
            disabled={deleteAccountMutation.isPending}
            className="w-full sm:w-auto gap-2"
          >
            {deleteAccountMutation.isPending ? 'Deleting...' : 'I understand, delete account'}
          </Button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
